import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import QuillTextEditor from "../../../../components/QuillTextEditor";
import TypeImageField from "../../Types/_components/TypeImageField";
import MemberRolesFields from "./MemberRolesFields";
import { buildMemberRoles } from "../utils/category.utils";

const CategoryForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Save Category",
  submittingLabel = "Saving...",
  isEditMode = false,
  readOnly = false,
  types = [],
  isTypesLoading = false,
  isTypesError = false,
  onSubmit,
  onFieldChange,
  onBack,
}) => {
  const disabled = submitting || readOnly;

  const handleMaxMembersChange = (rawValue) => {
    if (rawValue === "") {
      onFieldChange("max_members", "");
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;

    const next = String(Math.max(0, Math.floor(parsed)));
    onFieldChange("max_members", next);
    onFieldChange(
      "member_roles",
      buildMemberRoles(next, form.member_roles || [])
    );
  };

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => onFieldChange("title", e.target.value)}
              placeholder="Stock Prepaid"
              isInvalid={!!validationErrors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.title}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              The unique key is generated by the backend from the title.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={form.typeId}
              onChange={(e) => onFieldChange("typeId", e.target.value)}
              isInvalid={!!validationErrors.typeId}
              disabled={disabled || isTypesLoading || isTypesError}
            >
              <option value="">
                {isTypesLoading ? "Loading types..." : "No type"}
              </option>
              {types.map((type) => {
                const typeId = type._id || type.id;
                const label = type.name || type.title || `Type ${typeId}`;
                const statusLabel =
                  type.status === "inactive" ? " (inactive)" : "";
                return (
                  <option key={typeId} value={typeId}>
                    {label}
                    {statusLabel}
                  </option>
                );
              })}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.typeId}
            </Form.Control.Feedback>
            {isTypesError && (
              <Form.Text className="text-danger">
                Failed to load types. Please refresh and try again.
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              isInvalid={!!validationErrors.status}
              disabled={disabled}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {validationErrors.status}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Short description of this category"
              disabled={disabled}
            />
            {isEditMode ? (
              <Form.Text className="text-muted">
                Leave empty to clear the description.
              </Form.Text>
            ) : null}
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Consent form</Form.Label>
            <div className={disabled ? "opacity-75 pe-none" : ""}>
              <QuillTextEditor
                value={form.consent || ""}
                onChange={(next) => onFieldChange("consent", next)}
                placeholder="Write the consent form. Use {{driver_name}} for the driver name…"
                disabled={disabled}
              />
            </div>
            <Form.Text className="text-muted">
              Use {"{{driver_name}}"} where the driver&apos;s name should appear.
              Downloads are personalized per driver as a PDF with a signature
              block.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Engine CC</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Min CC <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              value={form.minCC}
              onChange={(e) => onFieldChange("minCC", e.target.value)}
              placeholder="0"
              isInvalid={!!validationErrors.minCC}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.minCC}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Max CC</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              value={form.maxCC}
              onChange={(e) => onFieldChange("maxCC", e.target.value)}
              placeholder="Optional"
              isInvalid={!!validationErrors.maxCC}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.maxCC}
            </Form.Control.Feedback>
            {isEditMode ? (
              <Form.Text className="text-muted">
                Leave empty to clear max CC.
              </Form.Text>
            ) : null}
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Rules</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Max Members</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="1"
              value={form.max_members}
              onChange={(e) => handleMaxMembersChange(e.target.value)}
              isInvalid={!!validationErrors.max_members}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.max_members}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex align-items-center">
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="navigator_allowed"
              label="Navigator allowed"
              checked={Boolean(form.navigator_allowed)}
              onChange={(e) =>
                onFieldChange("navigator_allowed", e.target.checked)
              }
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <MemberRolesFields
          form={form}
          onFieldChange={onFieldChange}
          disabled={disabled}
        />
      </Row>

      <h5 className="mb-3 mt-2">Image</h5>
      <TypeImageField
        label="Category Image"
        imagePath={form.image || ""}
        imageFile={form.imageFile}
        disabled={disabled}
        error={validationErrors.image}
        onFileChange={(file) => onFieldChange("imageFile", file)}
      />

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CategoryForm;
